import React, { useState, useRef, useEffect } from 'react';
import { HiOutlinePlayCircle } from 'react-icons/hi2';
import { IoLayers } from 'react-icons/io5';
import { MdEmail } from 'react-icons/md';
import { FaListUl, FaImage } from 'react-icons/fa6';

const TUTORIALS = [
  {
    id: 'extract-lists',
    title: 'Extract Lists | Scheduling and integrations',
    description: 'Learn to extract data from any list page, set up schedules, and connect integrations.',
    icon: FaListUl,
    color: 'amber',
    thumbnail: 'https://img.youtube.com/vi/oxJNTQsqztk/hqdefault.jpg',
    url: 'https://www.youtube.com/watch?v=oxJNTQsqztk',
    available: true,
  },
  {
    id: 'extract-page-details',
    title: 'Extract Page Details | Product names, prices, descriptions and more',
    description: 'Extract detailed data from product pages and more.',
    icon: IoLayers,
    color: 'blue',
    thumbnail: 'https://img.youtube.com/vi/ZKbUker8DD8/hqdefault.jpg',
    url: 'https://www.youtube.com/watch?v=ZKbUker8DD8',
    available: true,
  },
  {
    id: 'extract-emails',
    title: 'Extract Emails | Scan websites for emails in bulk',
    description: 'Extract email addresses from any website.',
    icon: MdEmail,
    color: 'emerald',
    thumbnail: null,
    url: null,
    available: false,
  },
  {
    id: 'download-images',
    title: 'Download Images | Scan pages for images and download',
    description: 'Bulk download images from any website or gallery.',
    icon: FaImage,
    color: 'purple',
    thumbnail: null,
    url: null,
    available: false,
  },
];

const COLOR_CLASSES = {
  amber:   { icon: 'text-amber-400 dark:text-amber-400',   bg: 'from-amber-500/10 to-amber-400/5 dark:from-amber-500/[0.07] dark:to-amber-400/[0.03]',   border: 'border-amber-300/30 dark:border-amber-400/15',   badge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-400/25',   dot: 'bg-amber-400' },
  blue:    { icon: 'text-blue-400 dark:text-blue-400',     bg: 'from-blue-500/10 to-blue-400/5 dark:from-blue-500/[0.07] dark:to-blue-400/[0.03]',     border: 'border-blue-300/30 dark:border-blue-400/15',     badge: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-400/25',     dot: 'bg-blue-400' },
  emerald: { icon: 'text-emerald-400 dark:text-emerald-400', bg: 'from-emerald-500/10 to-emerald-400/5 dark:from-emerald-500/[0.07] dark:to-emerald-400/[0.03]', border: 'border-emerald-300/30 dark:border-emerald-400/15', badge: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-400/25', dot: 'bg-emerald-400' },
  purple:  { icon: 'text-purple-400 dark:text-purple-400',   bg: 'from-purple-500/10 to-purple-400/5 dark:from-purple-500/[0.07] dark:to-purple-400/[0.03]',   border: 'border-purple-300/30 dark:border-purple-400/15',   badge: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-400/25',   dot: 'bg-purple-400' },
};

/**
 * TutorialsDropdown — hover-triggered floating panel for the Tutorials nav item.
 *
 * Props:
 *  - isOpen          {boolean}  controlled open state
 *  - onOpen          {function} called on mouseEnter (button or panel)
 *  - onCloseDelayed  {function} called on mouseLeave (150ms grace period handled by parent)
 *  - isActive        {boolean}  whether the pill should appear "active"
 */
const TutorialsDropdown = ({ isOpen, onOpen, onCloseDelayed, isActive }) => {
  return (
    <div className="relative">
      {/* Nav pill button */}
      <button
        onMouseEnter={onOpen}
        onMouseLeave={onCloseDelayed}
        title="Tutorials"
        className={`relative flex items-center gap-2 px-2.5 xl:px-3.5 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
          isActive || isOpen
            ? 'bg-gray-100 dark:bg-white/[0.08] text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-white/[0.08] shadow-sm dark:shadow-[0_0_12px_rgba(34,211,238,0.08)]'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.04]'
        }`}
      >
        <HiOutlinePlayCircle className="w-4 h-4" />
        <span className="hidden xl:inline">Tutorials</span>
      </button>

      {/* Floating panel */}
      {isOpen && (
        <div
          onMouseEnter={onOpen}
          onMouseLeave={onCloseDelayed}
          className="absolute left-1/2 top-full mt-2 z-50 w-[440px] max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/[0.08] rounded-2xl shadow-2xl shadow-black/10 dark:shadow-black/40 overflow-hidden ring-1 ring-inset ring-gray-100 dark:ring-white/[0.04]"
          style={{ transform: 'translateX(-50%)', animation: 'tutorialsPanelIn 0.2s ease-out' }}
        >
          {/* Gradient accent bar */}
          <div className="h-1 bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400" />

          <div className="p-4">
            {/* Header */}
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-purple-500/10 flex items-center justify-center">
                <HiOutlinePlayCircle className="w-4.5 h-4.5 text-cyan-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Video Tutorials</h3>
                <p className="text-[11px] text-gray-400 dark:text-gray-500">Learn how to get the most out of the platform</p>
              </div>
            </div>

            {/* Tutorial Cards */}
            <div className="space-y-2">
              {TUTORIALS.map((tutorial, index) => (
                <div
                  key={tutorial.id}
                  style={{ animation: `staggerIn 0.3s ease-out ${index * 60}ms both` }}
                >
                  {tutorial.available ? (
                    <a
                      href={tutorial.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex gap-3 p-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-white/[0.04] border border-transparent hover:border-gray-200/60 dark:hover:border-white/[0.06] transition-all duration-200 cursor-pointer"
                    >
                      {/* Thumbnail with play overlay */}
                      <div className="relative w-[120px] h-[68px] rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-gray-200/60 dark:ring-white/[0.08] shadow-sm">
                        <img
                          src={tutorial.thumbnail}
                          alt={tutorial.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/35 transition-colors">
                          <div className="w-9 h-9 rounded-full bg-white/90 dark:bg-white/80 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200">
                            <svg className="w-4 h-4 text-gray-900 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                          </div>
                        </div>
                      </div>
                      {/* Info */}
                      <div className="flex-1 flex flex-col justify-center min-w-0 gap-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors leading-snug">
                          {tutorial.title}
                        </p>
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-snug line-clamp-2">
                          {tutorial.description}
                        </p>
                        <span className="text-[10px] font-medium text-cyan-500 dark:text-cyan-400 flex items-center gap-1 mt-0.5">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                          Watch now
                        </span>
                      </div>
                    </a>
                  ) : (
                    <div className={`flex gap-3 p-2.5 rounded-xl border border-dashed ${COLOR_CLASSES[tutorial.color].border}`}>
                      {/* Placeholder thumbnail */}
                      <div className={`relative w-[120px] h-[68px] rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br ${COLOR_CLASSES[tutorial.color].bg} ring-1 ring-gray-200/40 dark:ring-white/[0.04] flex items-center justify-center`}>
                        <tutorial.icon className={`w-5 h-5 ${COLOR_CLASSES[tutorial.color].icon} opacity-60`} />
                      </div>
                      {/* Info */}
                      <div className="flex-1 flex flex-col justify-center min-w-0 gap-1.5">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {tutorial.title}
                        </p>
                        <span className="inline-flex self-start items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-purple-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-400/20">
                          <span className="w-1 h-1 rounded-full bg-cyan-400 animate-pulse" />
                          Coming Soon
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes tutorialsPanelIn {
            from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
            to   { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
          @keyframes staggerIn {
            from { opacity: 0; transform: translateY(6px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `,
      }} />
    </div>
  );
};

/**
 * TutorialsMobileList — inline tutorials list for the mobile navigation sheet.
 *
 * Props:
 *  - onItemClick {function} called when an available tutorial is clicked (to close mobile menu)
 */
const TutorialsMobileList = ({ onItemClick }) => (
  <div className="border-t border-gray-200/60 dark:border-white/[0.06] pt-2 mt-1">
    <p className="px-3 pb-1.5 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold">
      Tutorials
    </p>
    {TUTORIALS.map((tutorial) => (
      tutorial.available ? (
        <a
          key={tutorial.id}
          href={tutorial.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onItemClick}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
        >
          <tutorial.icon className={`w-[18px] h-[18px] ${COLOR_CLASSES[tutorial.color].icon}`} />
          <span className="flex-1 truncate">{tutorial.title}</span>
          <svg className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
          </svg>
        </a>
      ) : (
        <div
          key={tutorial.id}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 dark:text-gray-600"
        >
          <tutorial.icon className={`w-[18px] h-[18px] ${COLOR_CLASSES[tutorial.color].icon} opacity-50`} />
          <span className="flex-1 truncate">{tutorial.title}</span>
          <span className="text-[9px] px-1.5 py-0.5 bg-cyan-500/10 text-cyan-500 dark:text-cyan-400 rounded-md font-semibold uppercase tracking-wide flex-shrink-0">
            Soon
          </span>
        </div>
      )
    ))}
  </div>
);

export { TutorialsDropdown, TutorialsMobileList };
export default TutorialsDropdown;
